import { sumElementMultipliers } from "../../../utils/sumElementMultipliers";
import { isPlaceholderGroupName } from "../../../utils/isPlaceholderGroupName";
import { normalizeTypeName } from "./normalizeTypeName";
import type { PlanV3 } from "../../../utils/types";
import type {
  ComputeQuantityDiff,
  QuantityDiffEntry,
  QuantityDiffSet,
  QuantityDiffStatus,
} from "./types";

const ELEMENT_DECIMALS = 0;
const CABLE_DECIMALS = 1;

interface QuantityItem {
  name: string;
  quantity: number;
  lvPosition: string | null;
}

interface AggregatedItem extends QuantityItem {
  key: string;
}

export type DiffSide = "before" | "after";

// Agent placeholders ("Unbekannter Typ 3") repeat across plans without being the
// same type, so they are keyed per side and can never match each other.
export const buildMatchKey = (name: string, side: DiffSide): string => {
  const normalized = normalizeTypeName(name);
  return isPlaceholderGroupName(name) ? `${side}:${normalized}` : normalized;
};

const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

// Names that normalize identically are one type spelled inconsistently; merging
// them keeps a plan from matching the same counterpart twice.
const aggregateByNormalizedName = (
  items: QuantityItem[],
  side: DiffSide,
): Map<string, AggregatedItem> => {
  const itemsByKey = new Map<string, AggregatedItem>();

  for (const item of items) {
    const key = buildMatchKey(item.name, side);
    const existingItem = itemsByKey.get(key);

    if (!existingItem) {
      itemsByKey.set(key, { ...item, key });
      continue;
    }

    existingItem.quantity += item.quantity;
    existingItem.lvPosition = existingItem.lvPosition ?? item.lvPosition;
  }

  return itemsByKey;
};

// Driven by the quantities, not by the group's existence: a plan can carry a
// type whose markers are all gone, and that reads as removed, not changed.
const resolveStatus = (
  beforeQuantity: number,
  afterQuantity: number,
  delta: number,
): QuantityDiffStatus => {
  if (beforeQuantity > 0 && afterQuantity === 0) return "removed";
  if (beforeQuantity === 0 && afterQuantity > 0) return "added";
  return delta === 0 ? "unchanged" : "changed";
};

const buildEntry = (
  key: string,
  beforeItem: AggregatedItem | undefined,
  afterItem: AggregatedItem | undefined,
  decimals: number,
): QuantityDiffEntry => {
  const beforeQuantity = roundTo(beforeItem?.quantity ?? 0, decimals);
  const afterQuantity = roundTo(afterItem?.quantity ?? 0, decimals);
  // Deltas are derived from the rounded values so a row never reads "changed"
  // while both displayed numbers are identical.
  const delta = roundTo(afterQuantity - beforeQuantity, decimals);

  return {
    key,
    name: beforeItem?.name ?? afterItem?.name ?? "",
    lvPosition: beforeItem?.lvPosition ?? afterItem?.lvPosition ?? null,
    beforeQuantity,
    afterQuantity,
    delta,
    status: resolveStatus(beforeQuantity, afterQuantity, delta),
  };
};

export const STATUS_ORDER: Record<QuantityDiffStatus, number> = {
  added: 0,
  removed: 1,
  changed: 2,
  unchanged: 3,
};

const compareEntries = (
  firstEntry: QuantityDiffEntry,
  secondEntry: QuantityDiffEntry,
): number => {
  const statusDifference =
    STATUS_ORDER[firstEntry.status] - STATUS_ORDER[secondEntry.status];
  if (statusDifference !== 0) return statusDifference;

  if (firstEntry.status === "changed") {
    return Math.abs(secondEntry.delta) - Math.abs(firstEntry.delta);
  }

  const firstSize = Math.max(
    firstEntry.beforeQuantity,
    firstEntry.afterQuantity,
  );
  const secondSize = Math.max(
    secondEntry.beforeQuantity,
    secondEntry.afterQuantity,
  );
  return secondSize - firstSize;
};

const buildDiffSet = (
  beforeItems: QuantityItem[],
  afterItems: QuantityItem[],
  decimals: number,
): QuantityDiffSet => {
  const beforeByKey = aggregateByNormalizedName(beforeItems, "before");
  const afterByKey = aggregateByNormalizedName(afterItems, "after");

  const allEntries: QuantityDiffEntry[] = [];
  for (const [key, beforeItem] of beforeByKey) {
    allEntries.push(buildEntry(key, beforeItem, afterByKey.get(key), decimals));
  }
  for (const [key, afterItem] of afterByKey) {
    if (beforeByKey.has(key)) continue;
    allEntries.push(buildEntry(key, undefined, afterItem, decimals));
  }

  // Legend imports can register a type no marker was ever placed under; a row
  // that is empty in both plans has nothing to compare.
  const entries = allEntries.filter(
    (entry) => entry.beforeQuantity !== 0 || entry.afterQuantity !== 0,
  );

  entries.sort(compareEntries);

  const totals = entries.reduce(
    (accumulated, entry) => ({
      beforeQuantity: accumulated.beforeQuantity + entry.beforeQuantity,
      afterQuantity: accumulated.afterQuantity + entry.afterQuantity,
      delta: 0,
      changedEntryCount:
        accumulated.changedEntryCount + (entry.status === "unchanged" ? 0 : 1),
    }),
    {
      beforeQuantity: 0,
      afterQuantity: 0,
      delta: 0,
      changedEntryCount: 0,
    },
  );

  totals.beforeQuantity = roundTo(totals.beforeQuantity, decimals);
  totals.afterQuantity = roundTo(totals.afterQuantity, decimals);
  totals.delta = roundTo(
    totals.afterQuantity - totals.beforeQuantity,
    decimals,
  );

  return { entries, totals };
};

// NOTE: selection areas are not applied yet — these are plan-wide totals.
const buildElementItems = (plan: PlanV3): QuantityItem[] =>
  plan.groupedElements.groupIds.map((groupId) => ({
    name: groupId,
    quantity: sumElementMultipliers({
      elements: plan.groupedElements.byGroupId[groupId] ?? [],
    }),
    lvPosition: plan.groupMeta[groupId]?.lvMatch?.lvId ?? null,
  }));

const buildCableItems = (plan: PlanV3): QuantityItem[] =>
  plan.routeGroups.map((routeGroup) => ({
    name: routeGroup.label,
    quantity: routeGroup.meters,
    lvPosition: routeGroup.routes[0]?.lvMatch?.lvId ?? null,
  }));

export const computeQuantityDiff: ComputeQuantityDiff = ({
  beforePlan,
  afterPlan,
}) => ({
  elements: buildDiffSet(
    buildElementItems(beforePlan),
    buildElementItems(afterPlan),
    ELEMENT_DECIMALS,
  ),
  cables: buildDiffSet(
    buildCableItems(beforePlan),
    buildCableItems(afterPlan),
    CABLE_DECIMALS,
  ),
});
